/**
 * Schedule v2
 *
 * Typed schedule schemas with named rule slots and closed feature sets.
 *
 * A specialization (via {@link ScheduleV2Of}) narrows the generic
 * {@link ScheduleV2} but remains structurally compatible — a specialized
 * schedule _is_ a ScheduleV2.
 *
 * ## v1 → v2 changes
 *
 * **v1** ({@link ./schedule.ts}): flat `rules: Rule[]` array, open
 * `Record<string, string>` features, `*Rule` composites baking in `BaseRule`.
 *
 * **v2** (this module): named rule slots, each
 * `{ name, pattern, action, priority?, meta? }`. Features are closed and
 * typed. Actions are derived from feature definitions. Pattern data schemas
 * are reused from v1 but the composite wrappers are not.
 */
import { z } from "zod/v4";
import { isoDatetimeToDate } from "@visioni/a3s-codecs";
import { TimeZoneSchema } from "./date_time";
import { WeeklyPatternRuleSchema, RotatingPatternDefinitionSchema, AbsoluteRangeRuleSchema } from "./schedule";

// #region Metadata

export const ScheduleV2Metadata = z.object({
  updated_at: isoDatetimeToDate,
  precision: z.number().int().min(1).max(60),
  timezone: TimeZoneSchema,
});
export type ScheduleV2Metadata = z.infer<typeof ScheduleV2Metadata>;

// #endregion

// #region Patterns

export const WeeklyPatternV2 = z
  .object({ kind: z.literal("weekly_pattern") })
  .extend(WeeklyPatternRuleSchema.shape.weekly_pattern.shape);

export const RotatingPatternV2 = z
  .object({ kind: z.literal("rotating_pattern") })
  .extend(RotatingPatternDefinitionSchema.shape);

export const AbsolutePatternV2 = z
  .object({ kind: z.literal("absolute_range") })
  .extend(AbsoluteRangeRuleSchema.shape.absolute_range.shape);

// #endregion

// #region Schema builder

type FeatureDef<T extends z.ZodType = z.ZodType> = {
  schema: z.ZodObject<{ options: z.ZodType; default: z.ZodType }>;
  optionEnum: T;
};

type DefaultsShape = Record<string, FeatureDef>;
type FeaturesOf<D extends DefaultsShape> = { [K in keyof D]: D[K]["optionEnum"] };

const featureBuilder = {
  literal: <const T extends [string, ...string[]], D extends T[number]>(options: T, defaultValue: D) => {
    const optionEnum = z.enum(options);
    return {
      schema: z.object({
        options: z.array(optionEnum).nonempty().readonly(),
        default: z.literal(defaultValue),
      }),
      optionEnum,
    };
  },
};

const DefaultMetadata = z.record(z.string(), z.unknown()).optional();

const ruleBuilder = <F extends Record<string, z.ZodType>, M extends z.ZodType = typeof DefaultMetadata>(
  features: F,
  meta?: M,
) => {
  const name = z.string().max(100);
  const action = z.object(features).partial();
  const resolvedMeta = (meta ?? DefaultMetadata) as M;

  const priorityField = (defaultValue?: number) =>
    defaultValue !== undefined ? z.number().int().default(defaultValue) : z.number().int().optional();

  const ruleShape = <P extends z.ZodType>(pattern: P, opts?: { priority?: number }) => ({
    name,
    pattern,
    action,
    priority: priorityField(opts?.priority),
    meta: resolvedMeta,
  });

  return {
    array: <P extends z.ZodType>(pattern: P, opts?: { priority?: number }) =>
      z.array(z.object(ruleShape(pattern, opts))).readonly(),
    value: <P extends z.ZodType>(pattern: P, opts?: { priority?: number }) => z.object(ruleShape(pattern, opts)),
  };
};

/**
 * Create a typed schedule schema with specific features and named rule slots.
 *
 * @example
 * const AlarmSchedule = ScheduleV2Of({
 *   features: (f) => ({
 *     monitoring: f.literal(["armed", "disarmed"], "disarmed"),
 *   }),
 *   rules: (r) => ({
 *     routine: r.array(WeeklyPatternV2, { priority: 100 }),
 *     override: r.value(AbsolutePatternV2, { priority: 400 }).optional(),
 *   }),
 * });
 */
export const ScheduleV2Of = <
  D extends DefaultsShape,
  R extends Record<string, z.ZodType>,
  M extends z.ZodType = typeof DefaultMetadata,
>(config: {
  features: (feature: typeof featureBuilder) => D;
  rules: (rule: ReturnType<typeof ruleBuilder<FeaturesOf<D>, M>>) => R;
  metadata?: M;
}) => {
  const defaults = config.features(featureBuilder);
  const featureSchemas = Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, v.schema])) as {
    [K in keyof D]: D[K]["schema"];
  };
  const actionEnums = Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, v.optionEnum])) as FeaturesOf<D>;

  return z.object({
    schema_version: z.literal("2"),
    metadata: ScheduleV2Metadata,
    features: z.object(featureSchemas),
    rules: z.object(config.rules(ruleBuilder(actionEnums, config.metadata))),
  });
};

const ScheduleV2Feature = z.object({
  options: z.array(z.string()).readonly(),
  default: z.string(),
});

export const ScheduleV2Rule = z.object({
  name: z.string(),
  pattern: z.record(z.string(), z.unknown()),
  action: z.record(z.string(), z.string().optional()),
  priority: z.number().int().optional(),
  meta: z.record(z.string(), z.unknown()).optional(),
});

export type ScheduleV2Rule = {
  name: string;
  pattern: Record<string, unknown>;
  action: Record<string, string | undefined>;
  priority?: number | undefined;
  meta?: Record<string, unknown> | undefined;
};

export const ScheduleV2 = z.object({
  schema_version: z.literal("2"),
  metadata: ScheduleV2Metadata,
  features: z.record(z.string(), ScheduleV2Feature),
  rules: z.record(z.string(), z.union([z.array(ScheduleV2Rule).readonly(), ScheduleV2Rule]).optional()),
});

/**
 * Structural type for any v2 schedule — both the generic {@link ScheduleV2}
 * Zod schema output and any typed specialization from {@link ScheduleV2Of}.
 *
 * Use this as the parameter type when a function accepts any v2 schedule:
 * ```ts
 * function doSomething(schedule: ScheduleV2) { ... }
 * doSomething(myAlarmSchedule); // works without casts
 * ```
 */
export type ScheduleV2 = {
  schema_version: "2";
  metadata: ScheduleV2Metadata;
  features: { [k: string]: { options: readonly string[]; default: string } };
  rules: { [k: string]: ReadonlyArray<ScheduleV2Rule> | ScheduleV2Rule | undefined };
};

// Drift guards to stop Zod schema output diverging from the manual types
void (0 as unknown as keyof z.infer<typeof ScheduleV2Rule> satisfies keyof ScheduleV2Rule);
void (0 as unknown as keyof ScheduleV2Rule satisfies keyof z.infer<typeof ScheduleV2Rule>);
void (0 as unknown as z.infer<typeof ScheduleV2> satisfies ScheduleV2);

// #endregion
